import { usePublishedContext } from '@/components/views/published/PublishedContext';
import type { Item } from '@/lib/api/types/items';
import { useGetEstimationSchemeQuery } from '@/lib/queries/common/useGetEstimationScheme';
import { useItemPanelQuery } from '@/lib/queries/common/useItemPanelQuery';
import { useEstimationSchemesQueryWithPublished } from './useEstimationSchemesQueryWithPublished';
import { useEstimationsQueryWithPublished } from './useEstimationsQueryWithPublished';
import { useItemFetchQueryWithPublished } from './useItemFetchQueryWithPublished';
import { useItemStatusesQueryWithPublished } from './useItemStatusesQueryWithPublished';
import { useItemTypeLevelsQueryWithPublished } from './useItemTypeLevelsQueryWithPublished';
import { useItemTypesQueryWithPublished } from './useItemTypesQueryWithPublished';
import { useTeamsQueryWithPublished } from './useTeamsQueryWithPublished';

const PANEL_QUERY_OPTIONS = {
  refetchOnMount: false,
} as const;

export const useItemPanelQueryWithPublished = (item: Item, enabled = true) => {
  const publishedContext = usePublishedContext();

  const { data: itemStatuses = [] } = useItemStatusesQueryWithPublished({ enabled });
  const { data: itemTypes = [] } = useItemTypesQueryWithPublished({
    ...PANEL_QUERY_OPTIONS,
    enabled,
  });
  const { data: itemTypeLevels = [] } = useItemTypeLevelsQueryWithPublished({
    ...PANEL_QUERY_OPTIONS,
    enabled,
  });
  const { data: teams = [] } = useTeamsQueryWithPublished({ enabled });
  const { data: estimations = [] } = useEstimationsQueryWithPublished({ enabled });
  const { data: estimationSchemes = [] } = useEstimationSchemesQueryWithPublished({ enabled });

  const itemEstimation = item.estimationId
    ? estimations.find((e) => e.id === item.estimationId)
    : undefined;

  const publishedEstimationScheme = itemEstimation
    ? estimationSchemes.find((s) => s.id === itemEstimation.estimationSchemeId)
    : undefined;

  const { data: estimationScheme } = useGetEstimationSchemeQuery({
    schemeId: publishedContext === null ? (itemEstimation?.estimationSchemeId ?? 0) : 0,
  });

  const { data: parentItem } = useItemFetchQueryWithPublished({
    itemId: item.parentId ?? 0,
    enabled: enabled && item.parentId != null,
  });

  const panelResult = useItemPanelQuery(item, publishedContext === null && enabled);

  if (publishedContext !== null) {
    return {
      itemStatuses,
      itemTypes,
      itemTypeLevels,
      teams,
      parentItem,
      estimations,
      estimationScheme: publishedEstimationScheme,
    };
  }

  return { ...panelResult, itemTypeLevels, estimationScheme };
};
